import { useCallback, useMemo, useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { Edit, Copy, Trash2, HelpCircle, Eye, BarChart3, History } from 'lucide-react'
import { Tooltip } from '@heroui/react'
import { StatusIndicator } from '../shared/StatusIndicator'
import { FunnelBadge } from '../shared/FunnelBadge'
import { useFunnelAnalyticsMap } from '../../hooks/use-funnel-analytics-map'
import type { Funnel } from '../../types'

function formatCurrency(amount: number): string {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(amount)
}

function formatNumber(value: number): string {
  return new Intl.NumberFormat('en-US').format(value)
}

interface FunnelTableProps {
  funnels: Funnel[]
  onDelete: (id: string) => void
  onDuplicate: (id: string) => void
  onBulkDelete?: (ids: string[]) => void
  onBulkDuplicate?: (ids: string[]) => void
}

export function FunnelTable({
  funnels,
  onDelete,
  onDuplicate,
  onBulkDelete,
  onBulkDuplicate,
}: FunnelTableProps) {
  const navigate = useNavigate()
  const handleNavigate = useCallback((id: string) => navigate(`/funnels/${id}`), [navigate])
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set())

  const funnelIds = useMemo(() => funnels.map((f) => f.id), [funnels])
  const { analyticsMap: funnelAnalyticsMap } = useFunnelAnalyticsMap(funnelIds)
  const totalVisits = useMemo(() => {
    return funnels.reduce((sum, funnel) => sum + (funnelAnalyticsMap.get(funnel.id)?.impressions ?? 0), 0)
  }, [funnels, funnelAnalyticsMap])

  const allSelected = useMemo(() => {
    return funnels.length > 0 && funnels.every((funnel) => selectedIds.has(funnel.id))
  }, [funnels, selectedIds])

  const someSelected = useMemo(() => {
    return funnels.some((funnel) => selectedIds.has(funnel.id)) && !allSelected
  }, [funnels, selectedIds, allSelected])

  const handleSelectAll = useCallback(() => {
    if (allSelected) {
      setSelectedIds(new Set())
    } else {
      setSelectedIds(new Set(funnels.map((funnel) => funnel.id)))
    }
  }, [allSelected, funnels])

  const handleSelectRow = useCallback((id: string) => {
    setSelectedIds((prev) => {
      const next = new Set(prev)
      if (next.has(id)) {
        next.delete(id)
      } else {
        next.add(id)
      }
      return next
    })
  }, [])

  const handleBulkDelete = useCallback(() => {
    if (onBulkDelete) {
      onBulkDelete(Array.from(selectedIds))
      setSelectedIds(new Set())
    }
  }, [selectedIds, onBulkDelete])

  const handleBulkDuplicate = useCallback(() => {
    if (onBulkDuplicate) {
      onBulkDuplicate(Array.from(selectedIds))
      setSelectedIds(new Set())
    }
  }, [selectedIds, onBulkDuplicate])

  const handleClearSelection = useCallback(() => {
    setSelectedIds(new Set())
  }, [])

  // Context menu on right-click
  const [contextMenu, setContextMenu] = useState<{ x: number; y: number; funnelId: string } | null>(null)

  useEffect(() => {
    if (!contextMenu) return
    const dismiss = () => setContextMenu(null)
    document.addEventListener('mousedown', dismiss)
    return () => document.removeEventListener('mousedown', dismiss)
  }, [contextMenu])

  const handleRowContextMenu = useCallback((e: React.MouseEvent, funnelId: string) => {
    e.preventDefault()
    setContextMenu({ x: e.clientX, y: e.clientY, funnelId })
  }, [])

  const thClass = 'text-left px-4 py-3 font-medium text-fn-text-sub uppercase text-xs tracking-wide whitespace-nowrap'

  const hasSelection = selectedIds.size > 0

  return (
    <>
      <div className="overflow-x-auto rounded-lg border border-fn-border">
        <table className="w-full text-sm">
          <thead>
            <tr className={hasSelection ? 'bg-fn-selected' : 'bg-fn-muted'}>
              <th className={`${thClass} w-10`}>
                <input
                  type="checkbox"
                  checked={allSelected}
                  ref={(el) => { if (el) el.indeterminate = someSelected }}
                  onChange={handleSelectAll}
                  aria-label="Select all funnels"
                  className="w-3.5 h-3.5 rounded border-tf-border text-primary focus:ring-primary cursor-pointer"
                />
              </th>
              {hasSelection ? (
                <th colSpan={11} className="px-4 py-3 text-left">
                  <div className="flex items-center gap-2">
                    <span className="text-sm font-medium text-blue-800">{selectedIds.size} selected</span>
                    <div className="ml-auto flex items-center gap-2">
                      {onBulkDuplicate && (
                        <button
                          onClick={handleBulkDuplicate}
                          className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium text-fn-text-sub bg-fn-card border border-fn-border rounded-md hover:bg-fn-hover transition-colors"
                        >
                          <Copy className="w-3.5 h-3.5" />
                          Duplicate
                        </button>
                      )}
                      {onBulkDelete && (
                        <button
                          onClick={handleBulkDelete}
                          className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium text-red-600 bg-tf-bg-card border border-tf-feedback-error-border rounded-md hover:bg-tf-danger-light transition-colors"
                        >
                          <Trash2 className="w-3.5 h-3.5" />
                          Delete
                        </button>
                      )}
                      <button
                        onClick={handleClearSelection}
                        className="px-3 py-1.5 text-sm font-medium text-fn-text-muted hover:text-fn-text-sub transition-colors"
                      >
                        Clear
                      </button>
                    </div>
                  </div>
                </th>
              ) : (
                <>
                  <th className={thClass}>
                    <span className="flex items-center gap-1">
                      PRODUCT
                      <Tooltip content="Primary product linked to this funnel">
                        <HelpCircle className="w-3.5 h-3.5 text-fn-text-muted cursor-help" />
                      </Tooltip>
                    </span>
                  </th>
                  <th className={thClass}>
                    <span className="flex items-center gap-1">
                      FUNNEL TYPE
                      <Tooltip content="Main type of funnel experience">
                        <HelpCircle className="w-3.5 h-3.5 text-fn-text-muted cursor-help" />
                      </Tooltip>
                    </span>
                  </th>
                  <th className={thClass}>
                    <span className="flex items-center gap-1">
                      CONVERSION %
                      <Tooltip content="Percentage of visits that converted">
                        <HelpCircle className="w-3.5 h-3.5 text-fn-text-muted cursor-help" />
                      </Tooltip>
                    </span>
                  </th>
                  <th className={thClass}>
                    <span className="flex items-center gap-1">
                      REVENUE
                      <Tooltip content="Total revenue generated by this funnel">
                        <HelpCircle className="w-3.5 h-3.5 text-fn-text-muted cursor-help" />
                      </Tooltip>
                    </span>
                  </th>
                  <th className={thClass}>
                    <span className="flex items-center gap-1">
                      AOV
                      <Tooltip content="Average order value">
                        <HelpCircle className="w-3.5 h-3.5 text-fn-text-muted cursor-help" />
                      </Tooltip>
                    </span>
                  </th>
                  <th className={thClass}>
                    <span className="flex items-center gap-1">
                      RPV
                      <Tooltip content="Revenue per visit">
                        <HelpCircle className="w-3.5 h-3.5 text-fn-text-muted cursor-help" />
                      </Tooltip>
                    </span>
                  </th>
                  <th className={thClass}>
                    <span className="flex items-center gap-1">
                      VISITS
                      <Tooltip content="Total visits for this funnel">
                        <HelpCircle className="w-3.5 h-3.5 text-fn-text-muted cursor-help" />
                      </Tooltip>
                    </span>
                  </th>
                  <th className={thClass}>
                    <span className="flex items-center gap-1">
                      VISITS SHARE
                      <Tooltip content="Share of total visits in current list">
                        <HelpCircle className="w-3.5 h-3.5 text-fn-text-muted cursor-help" />
                      </Tooltip>
                    </span>
                  </th>
                  <th className={thClass}>
                    <span className="flex items-center gap-1">
                      ACTIVE STEPS
                      <Tooltip content="Number of active steps in this funnel">
                        <HelpCircle className="w-3.5 h-3.5 text-fn-text-muted cursor-help" />
                      </Tooltip>
                    </span>
                  </th>
                  <th className={thClass}>STATUS</th>
                  <th className={thClass}>ACTIONS</th>
                </>
              )}
            </tr>
          </thead>
          <tbody>
            {funnels.length === 0 ? (
              <tr>
                <td colSpan={12} className="px-4 py-12 text-center text-fn-text-muted">
                  No funnels found.
                </td>
              </tr>
            ) : (
              funnels.map((funnel) => (
                <tr
                  key={funnel.id}
                  className={`border-t border-fn-border cursor-pointer transition-colors animate-fade-slide-in ${
                    selectedIds.has(funnel.id) ? 'bg-tf-feedback-info-bg' : 'hover:bg-fn-hover'
                  }`}
                  onClick={() => handleNavigate(funnel.id)}
                  onContextMenu={(e) => handleRowContextMenu(e, funnel.id)}
                >
                  {(() => {
                    const analytics = funnelAnalyticsMap.get(funnel.id)
                    const visits = analytics?.impressions ?? 0
                    const revenue = analytics?.revenue ?? 0
                    const conversionRate = analytics?.conversionRate ?? 0
                    const aov = analytics?.avgOrderValue ?? 0
                    const rpv = visits > 0 ? revenue / visits : 0
                    const visitsShare = totalVisits > 0 ? (visits / totalVisits) * 100 : 0
                    const activeSteps = analytics?.stepsCount ?? funnel.steps?.length ?? 0

                    return (
                      <>
                  <td className="px-4 py-3" onClick={(e) => e.stopPropagation()}>
                    <input
                      type="checkbox"
                      checked={selectedIds.has(funnel.id)}
                      onChange={() => handleSelectRow(funnel.id)}
                      aria-label={`Select ${funnel.name}`}
                      className="w-3.5 h-3.5 rounded border-tf-border text-primary focus:ring-primary cursor-pointer"
                    />
                  </td>
                  <td className="px-4 py-3">
                    <div className="font-medium text-fn-text">{funnel.productId || '-'}</div>
                    <div className="text-xs mt-0.5 text-fn-text-muted">
                      {funnel.name}
                    </div>
                  </td>
                  <td className="px-4 py-3">
                    {funnel.funnelType ? (
                      <FunnelBadge type="funnel" value={funnel.funnelType} />
                    ) : (
                      <span className="text-fn-text-muted">-</span>
                    )}
                  </td>
                  <td className="px-4 py-3">
                    <span className="text-fn-text">{conversionRate.toFixed(2)}%</span>
                  </td>
                  <td className="px-4 py-3">
                    <span className="text-fn-text">{formatCurrency(revenue)}</span>
                  </td>
                  <td className="px-4 py-3">
                    <span className="text-fn-text">{formatCurrency(aov)}</span>
                  </td>
                  <td className="px-4 py-3">
                    <span className="text-fn-text">{formatCurrency(rpv)}</span>
                  </td>
                  <td className="px-4 py-3">
                    <span className="text-fn-text">{formatNumber(visits)}</span>
                  </td>
                  <td className="px-4 py-3">
                    <span className="text-fn-text">{visitsShare.toFixed(2)}%</span>
                  </td>
                  <td className="px-4 py-3">
                    <span className="text-fn-text">{formatNumber(activeSteps)}</span>
                  </td>
                  <td className="px-4 py-3">
                    <StatusIndicator isActive={funnel.isActive} />
                  </td>
                  <td className="px-4 py-3" onClick={(e) => e.stopPropagation()}>
                    <div className="flex items-center gap-1.5">
                      <button
                        onClick={() => handleNavigate(funnel.id)}
                        className="p-1.5 rounded-md hover:bg-fn-hover text-fn-text-muted hover:text-fn-text-sub transition-colors"
                        aria-label="Edit funnel"
                      >
                        <Edit className="w-4 h-4" />
                      </button>
                      <button
                        onClick={() => handleNavigate(funnel.id)}
                        className="p-1.5 rounded-md hover:bg-fn-hover text-fn-text-muted hover:text-fn-text-sub transition-colors"
                        aria-label="Flow view"
                      >
                        <Eye className="w-4 h-4" />
                      </button>
                      <button
                        onClick={() => navigate('/funnels/dashboard')}
                        className="p-1.5 rounded-md hover:bg-fn-hover text-fn-text-muted hover:text-fn-text-sub transition-colors"
                        aria-label="Analytics"
                      >
                        <BarChart3 className="w-4 h-4" />
                      </button>
                      <button
                        onClick={() => navigate('/funnels/changelog')}
                        className="p-1.5 rounded-md hover:bg-fn-hover text-fn-text-muted hover:text-fn-text-sub transition-colors"
                        aria-label="History"
                      >
                        <History className="w-4 h-4" />
                      </button>
                    </div>
                  </td>
                      </>
                    )
                  })()}
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {contextMenu && (
        <>
          <div className="fixed inset-0 z-40" onClick={() => setContextMenu(null)} onContextMenu={(e) => { e.preventDefault(); setContextMenu(null) }} />
          <div
            className="fixed w-44 bg-tf-bg-card rounded-lg shadow-lg border border-tf-border py-1 z-50 animate-scale-in"
            style={{ top: contextMenu.y, left: contextMenu.x }}
          >
            {[
              { label: 'View', icon: <Eye className="w-4 h-4" />, onClick: () => handleNavigate(contextMenu.funnelId) },
              { label: 'Edit', icon: <Edit className="w-4 h-4" />, onClick: () => handleNavigate(contextMenu.funnelId) },
              { label: 'Analytics', icon: <BarChart3 className="w-4 h-4" />, onClick: () => navigate('/funnels/dashboard') },
              { label: 'History', icon: <History className="w-4 h-4" />, onClick: () => navigate('/funnels/changelog') },
              { label: 'Duplicate', icon: <Copy className="w-4 h-4" />, onClick: () => onDuplicate(contextMenu.funnelId) },
              { label: 'Delete', icon: <Trash2 className="w-4 h-4" />, onClick: () => onDelete(contextMenu.funnelId), danger: true },
            ].map((item, i, arr) => (
              <button
                key={item.label}
                onClick={() => { item.onClick(); setContextMenu(null) }}
                className={`w-full flex items-center gap-2.5 px-3 py-2 text-sm transition-colors ${
                  item.danger ? 'text-red-600 hover:bg-red-50 dark:hover:bg-red-950/30' : 'text-tf-text-sub hover:bg-tf-bg-hover'
                } ${i === arr.length - 1 ? 'border-t border-tf-border mt-1 pt-2' : ''}`}
              >
                {item.icon}
                {item.label}
              </button>
            ))}
          </div>
        </>
      )}
    </>
  )
}
